import { Transition } from '@headlessui/react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { QRCode } from 'react-qrcode-logo'

import { useState } from 'react'
import Button from '../../../../components/tailwind/Button'
import { Heading } from '../../../../components/ui/Heading'
import { isSet } from '../../../../helpers'

// h2 und Listen kommen z. B. mit dem detail-Text eines Kulturwerts
// (DIMENSIONS_CULTURE). Ohne eigene Styles setzt Preflight sie auf Fließtext
// zurück und entfernt die Aufzählungspunkte.
const MARKDOWN_COMPONENTS = {
  p: ({ children }) => (
    <p className='mb-5 block text-sm/normal text-darkBlack'>{children}</p>
  ),
  h2: ({ children }) => (
    <h2 className='mb-3 text-base font-semibold text-darkBlack'>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className='mb-2 text-sm font-semibold text-darkBlack'>{children}</h3>
  ),
  ul: ({ children }) => (
    <ul className='mb-5 list-outside list-disc space-y-1 pl-4'>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className='mb-5 list-outside list-decimal space-y-1 pl-4'>
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className='text-sm/normal text-darkBlack'>{children}</li>
  )
}

export default function SystemMessage({
  className,
  title,
  description,
  cta,
  imageSrc,
  withQrCode
}) {
  const { t } = useTranslation()

  const [ctaVisible, setCtaVisible] = useState(true)

  const handleCtaClick = (onClick) => {
    setCtaVisible(false)
    setTimeout(onClick, 300)
  }

  return (
    <div
      className={clsx(
        'w-full rounded-lg border border-lightGrey bg-lightPaleGrey p-4',
        className
      )}
    >
      {title && <Heading level={5}>{title}</Heading>}
      {imageSrc && (
        <div className='my-4 flex justify-center'>
          <img className='h-auto max-w-full' src={imageSrc} alt={title} />
        </div>
      )}
      {description && (
        <ReactMarkdown components={MARKDOWN_COMPONENTS}>
          {description}
        </ReactMarkdown>
      )}
      <Transition
        as={'div'}
        show={ctaVisible}
        enter='transition-opacity duration-300'
        enterFrom='opacity-0'
        enterTo='opacity-100'
        leave='transition-opacity duration-300'
        leaveFrom='opacity-100'
        leaveTo='opacity-0'
      >
        {cta && (
          <div className='mt-4 flex flex-col gap-y-2'>
            <Button.PrimaryBase
              text={cta.title}
              isPulsing={cta.isPulsing || false}
              onClick={() => handleCtaClick(cta.onClick)}
            />
          </div>
        )}
      </Transition>

      {isSet(withQrCode) && (
        <div className='mt-4 hidden flex-col items-start border-t border-gray-200 pt-4 sm:flex'>
          <Heading level={5}>
            {t(
              'discover.assistant.system_message.explore_strengths.4.qr_code.title'
            )}
          </Heading>
          <p className='mt-1 text-sm font-normal text-darkBlack'>
            {t(
              'discover.assistant.system_message.explore_strengths.4.qr_code.description'
            )}
          </p>
          <div className='mt-2'>
            <QRCode
              fgColor='#283440'
              value={withQrCode}
              size={128}
              logoImage='https://aivy-assets.s3.eu-central-1.amazonaws.com/logo/aivylogo.png'
              logoWidth={80}
              style={{
                borderWidth: 1,
                borderColor: '##D1DBE6',
                borderRadius: 8
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

SystemMessage.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  cta: PropTypes.shape({
    title: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    isPulsing: PropTypes.bool
  }),
  withQrCode: PropTypes.string,
  imageSrc: PropTypes.string
}
